<template>
    <div class="flex flex-column gap-2">
        <div class="flex flex-column sm:flex-row sm:align-items-center justify-content-between gap-3">
            <span class="text-lg font-bold">{{ $t('lang.pages.tariff_limits.limits_count') }}</span>
            <div class="limits-input__field flex align-items-center">
                <Button
                    icon="pi pi-minus"
                    text
                    severity="secondary"
                    :disabled="modelValue <= LIMITS_MIN"
                    @click="onChange(modelValue - LIMITS_STEP)"
                />
                <InputText
                    v-model="inputText"
                    class="limits-input__input"
                    inputmode="numeric"
                    @input="onInput"
                    @blur="onApplyInput"
                    @keydown.enter="onApplyInput"
                />
                <Button
                    icon="pi pi-plus"
                    text
                    severity="secondary"
                    :disabled="modelValue >= LIMITS_MAX"
                    @click="onChange(modelValue + LIMITS_STEP)"
                />
            </div>
        </div>
        <div class="limits-input__slider">
            <Slider v-model="sliderPosition" :min="0" :max="SLIDER_MAX" />
            <div class="limits-input__marks">
                <span
                    v-for="(mark, index) in LIMITS_MARKS"
                    :key="mark"
                    :style="{left: getMarkPosition(index) + '%'}"
                >
                    <span class="limits-input__mark_full">{{ formatNumber(mark) }}</span>
                    <span class="limits-input__mark_short">{{ formatNumberShort(mark) }}</span>
                </span>
            </div>
        </div>
        <span class="text-xs text-color-secondary">
            {{ $t('lang.pages.tariff_limits.limits_hint', [formatNumber(LIMITS_STEP)]) }}
        </span>
    </div>
</template>

<script>
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Slider from "primevue/slider";
import {FormatHelper} from "keysso/helpers/FormatHelper";

const LIMITS_STEP = 5000;
const LIMITS_MIN = 5000;
const LIMITS_MAX = 1000000;
// отметки неравномерной шкалы, каждая занимает равный отрезок ползунка
const LIMITS_MARKS = [5000, 25000, 75000, 100000, 500000, 1000000];
const SLIDER_MAX = 1000;
const SEGMENT_SIZE = SLIDER_MAX / (LIMITS_MARKS.length - 1);

export default {
    name: "TariffLimitsInput",
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            LIMITS_STEP,
            LIMITS_MIN,
            LIMITS_MAX,
            LIMITS_MARKS,
            SLIDER_MAX,
            inputText: '',
        }
    },
    computed: {
        sliderPosition: {
            get() {
                const index = Math.max(LIMITS_MARKS.findIndex(mark => this.modelValue <= mark) - 1, 0);
                const from = LIMITS_MARKS[index];
                const to = LIMITS_MARKS[index + 1];

                return (index + (this.modelValue - from) / (to - from)) * SEGMENT_SIZE;
            },
            set(position) {
                const index = Math.min(Math.floor(position / SEGMENT_SIZE), LIMITS_MARKS.length - 2);
                const from = LIMITS_MARKS[index];
                const to = LIMITS_MARKS[index + 1];

                this.onChange(from + (position / SEGMENT_SIZE - index) * (to - from));
            },
        },
    },
    watch: {
        modelValue: {
            handler(value) {
                this.inputText = this.formatNumber(value);
            },
            immediate: true,
        },
    },
    methods: {
        formatNumber(number) {
            return FormatHelper.number(number);
        },
        formatNumberShort(number) {
            return FormatHelper.numberDigits(number);
        },
        onChange(value) {
            const rounded = Math.round(value / LIMITS_STEP) * LIMITS_STEP;
            this.$emit('update:modelValue', Math.min(Math.max(rounded, LIMITS_MIN), LIMITS_MAX));
        },
        onInput() {
            this.inputText = this.inputText.replace(/\D/g, '');
        },
        onApplyInput() {
            const value = Number(this.inputText.replace(/\D/g, ''));
            this.onChange(value || LIMITS_MIN);
            // значение могло не измениться после округления, тогда watch не сработает
            this.inputText = this.formatNumber(this.modelValue);
        },
        getMarkPosition(index) {
            return index * 100 / (LIMITS_MARKS.length - 1);
        },
    },
    components: {
        Button,
        InputText,
        Slider,
    },
}
</script>

<style scoped>
.limits-input__field {
    background: #F0F4F7;
    border-radius: 8px;
    padding: 4px;
}
.limits-input__input {
    width: 120px;
    border: none;
    background: transparent;
    box-shadow: none;
    text-align: center;
    font-size: 18px;
    font-weight: 700;
    color: #232D35;
}
.limits-input__slider {
    padding: 8px 8px 0;
}
.limits-input__marks {
    position: relative;
    height: 21px;
    margin-top: 12px;
}
.limits-input__marks > span {
    position: absolute;
    transform: translateX(-50%);
    font-size: 12px;
    line-height: 21px;
    color: #A4B1BE;
    white-space: nowrap;
}
.limits-input__marks > span:first-child {
    transform: translateX(-8px);
}
.limits-input__mark_short {
    display: none;
}
.limits-input__marks > span:last-child {
    transform: translateX(calc(-100% + 8px));
}

@media (max-width: 576px) {
    .limits-input__field {
        justify-content: space-between;
    }
    .limits-input__input {
        flex: 1;
    }
    .limits-input__mark_full {
        display: none;
    }
    .limits-input__mark_short {
        display: inline;
    }
}
</style>
